import { useQuery } from "@tanstack/react-query";

import {
  ASQAmount,
  ErrorState,
  Panel,
  SheetLabel,
  Skeleton,
  Status,
  toneForStatus,
  utilityLabel,
} from "@/components/primitives";
import { api } from "@/lib/api";
import { formatASQ, formatDateTime, idFragment } from "@/lib/format";

/**
 * Protocol — plan §38, §37A.
 *
 * A registry infrastructure map rather than a table of cards: each utility is
 * one connected row that reads left to right — registry id, module, vault,
 * locked — because that is the actual topology and a card grid hides it.
 *
 * The supply arithmetic is printed as an equation rather than as three
 * statistics, because the relationship between the numbers is the claim being
 * made: total supply never moves, locked only rises, and effective supply is
 * the difference.
 */
export function ProtocolPage() {
  const { data, isPending, isError, error } = useQuery({
    queryKey: ["protocol"],
    queryFn: api.protocol,
    refetchInterval: 20_000,
  });

  if (isPending) {
    return (
      <div className="sheet space-y-6 py-16">
        <Skeleton className="h-12 w-80" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="sheet py-16">
        <ErrorState title="Registry tidak dapat dibaca" code="REGISTRY_UNREADABLE">
          {error instanceof Error ? error.message : "Node RPC tidak menjawab."}
        </ErrorState>
      </div>
    );
  }

  return (
    <div className="sheet py-16">
      <SheetLabel coordinate={`CHAIN ${data.chainId}`}>Registry infrastructure</SheetLabel>
      <h1 className="display-section mt-5 text-paper-50">
        ASETQU Protocol
        <br />
        Test Environment
      </h1>

      {/* The equation. One dominant metric plus its two operands (§62). */}
      <Panel tone="paper" marked className="mt-12 p-8 lg:p-10">
        <p className="micro">Effective supply</p>
        <p className="mt-4">
          <span className="datum-xl text-ink-950">
            {formatASQ(data.effectiveRaw, { maxFractionDigits: 2 })}
            <span className="ml-2 font-mono text-[0.3em] align-super tracking-[0.2em]">ASQ</span>
          </span>
        </p>

        <div className="mt-8 grid gap-6 border-t border-paper-300 pt-6 sm:grid-cols-3">
          <div>
            <p className="micro">Total supply</p>
            <p className="datum mt-2 text-lg text-ink-950">
              {formatASQ(data.totalSupplyRaw, { maxFractionDigits: 2 })}
            </p>
          </div>
          <div>
            <p className="micro">− Terkunci permanen</p>
            <p className="datum mt-2 text-lg text-ink-950">
              {formatASQ(data.totalLockedRaw, { maxFractionDigits: 2 })}
            </p>
          </div>
          <div>
            <p className="micro">= Effective</p>
            <p className="datum mt-2 text-lg text-ink-950">
              {formatASQ(data.effectiveRaw, { maxFractionDigits: 2 })}
            </p>
          </div>
        </div>

        {/*
          Said out loud rather than left as a footnote nobody reads. "Total
          locked" here is three vaults, not the ecosystem — and someone quoting
          this figure as ecosystem-wide would be quoting it wrong.
        */}
        <p className="mt-6 text-xs text-ink-800/70">{data.lockedScopeNote}</p>
      </Panel>

      {/* The map. */}
      <section className="mt-14">
        <SheetLabel>Utility records</SheetLabel>

        <ul className="mt-6 border-t border-ink-700">
          {data.utilities.map((utility) => (
            <li key={utility.name} className="border-b border-ink-700 py-6">
              <div className="grid gap-5 lg:grid-cols-12 lg:items-center">
                <div className="lg:col-span-3">
                  <p className="micro text-hub-700">{idFragment(utility.id)}</p>
                  <p className="display-section mt-2 text-lg text-paper-50">
                    {utilityLabel(utility.name)}
                  </p>
                  <p className="micro mt-1.5">v{utility.version}</p>
                </div>

                <div className="lg:col-span-2">
                  <Status state={toneForStatus(utility.status)}>{utility.status}</Status>
                </div>

                {/*
                  The topology, drawn: module → vault, with the rail's terminal
                  block closing it off. Same language as the payment sheet,
                  because it is the same relationship.
                */}
                <div className="lg:col-span-4">
                  <p className="micro">Module</p>
                  <p className="hexline mt-1 opacity-70">
                    {utility.moduleUrl ? (
                      <a
                        className="linkline text-hub-300"
                        href={utility.moduleUrl}
                        target="_blank"
                        rel="noreferrer noopener"
                      >
                        {utility.module}
                      </a>
                    ) : (
                      utility.module
                    )}
                  </p>

                  <div className="rail my-2.5" aria-hidden="true">
                    <span className="rail__origin" />
                    <span className="rail__line" />
                    <span className="rail__terminal" />
                  </div>

                  <p className="micro">Permanent vault</p>
                  <p className="hexline mt-1 opacity-70">
                    {utility.vaultUrl ? (
                      <a
                        className="linkline text-hub-300"
                        href={utility.vaultUrl}
                        target="_blank"
                        rel="noreferrer noopener"
                      >
                        {utility.vault}
                      </a>
                    ) : (
                      utility.vault
                    )}
                  </p>
                </div>

                <div className="lg:col-span-3 lg:text-right">
                  <p className="micro">Terkunci</p>
                  <p className="mt-2">
                    <ASQAmount raw={utility.lockedRaw} size="lg" maxFractionDigits={2} />
                  </p>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </section>

      <dl className="mt-12 grid gap-6 sm:grid-cols-2">
        <div>
          <dt className="micro">UtilityRegistry</dt>
          <dd className="hexline mt-2 opacity-70">
            {data.registryUrl ? (
              <a
                className="linkline text-hub-300"
                href={data.registryUrl}
                target="_blank"
                rel="noreferrer noopener"
              >
                {data.registry}
              </a>
            ) : (
              data.registry
            )}
          </dd>
        </div>
        <div>
          <dt className="micro">ASQ Token</dt>
          <dd className="hexline mt-2 opacity-70">
            {data.asqTokenUrl ? (
              <a
                className="linkline text-hub-300"
                href={data.asqTokenUrl}
                target="_blank"
                rel="noreferrer noopener"
              >
                {data.asqToken}
              </a>
            ) : (
              data.asqToken
            )}
          </dd>
        </div>
        <div>
          <dt className="micro">Network</dt>
          <dd className="datum mt-2 text-xs opacity-70">
            {data.network} · chain {data.chainId}
          </dd>
        </div>
        <div>
          <dt className="micro">Dibaca</dt>
          <dd className="datum mt-2 text-xs opacity-70">{formatDateTime(data.readAt)}</dd>
        </div>
      </dl>
    </div>
  );
}
